import { Box, Flex, FormLabel, Switch, Text } from "@chakra-ui/react";
import * as DateFns from "date-fns";
import { useFormikContext } from "formik";
import { SelectControl } from "formik-chakra-ui";
import React from "react";
import { DateTypeFormats, DEFAULT_DATE_TYPE_FORMAT } from "src/constants/date";
import { isNonEmptyString, isNullOrUndefined } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import { InputControlWithQuestionId } from "../../InputControlWithQuestionId";
import * as QuestionType from "../QuestionType/formik";

type Props = {
  questionId: uuid;
};
export const DateFormat: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();

  React.useEffect(() => {
    (async () => {
      if (formik.values.type === "Date") {
        await formik.setFieldValue(
          "dateFormat",
          formik.values.dateFormat ?? DEFAULT_DATE_TYPE_FORMAT
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.type]);

  const id = `dateFormat-${questionId}`;
  return (
    <Flex direction="column" gap="2">
      <SelectControl
        label="Date format"
        name="dateFormat"
        labelProps={{ htmlFor: id }}
        selectProps={{ id }}
      >
        {DateTypeFormats.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </SelectControl>
      <Text fontSize="sm" textColor="gray.600">
        This date will be displayed using the selected format on exports.
        Example:{" "}
        {DateFns.formatDate(
          Date.now(),
          isNonEmptyString(formik.values.dateFormat)
            ? formik.values.dateFormat
            : DEFAULT_DATE_TYPE_FORMAT
        )}
      </Text>
    </Flex>
  );
};

export const DateConstraints: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();

  if (formik.values.dateConstraints.switch === "off") {
    return null;
  }

  return (
    <Flex direction="column" gap="2">
      <Flex direction="row" gap="4">
        <InputControlWithQuestionId
          questionId={questionId}
          label="Start date (optional)"
          name="dateConstraints.startDate"
          inputProps={{ type: "date" }}
        />
        <InputControlWithQuestionId
          questionId={questionId}
          label="End date (optional)"
          name="dateConstraints.endDate"
          inputProps={{
            type: "date",
          }}
        />
      </Flex>
      <Text fontSize="sm" textColor="gray.600">
        The parent will only be able to choose a date between the two dates
        above.
      </Text>
    </Flex>
  );
};

export const DateConstraintsSwitch: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();

  return (
    <FormLabel
      htmlFor={`dateConstraintsSwitch${questionId}`}
      display="flex"
      flexDirection="row"
      gap="3"
    >
      <Switch
        id={`dateConstraintsSwitch${questionId}`}
        isChecked={formik.values.dateConstraints.switch !== "off"}
        onChange={async () => {
          await formik.setFieldValue(
            "dateConstraints.switch",
            formik.values.dateConstraints.switch === "on" ? "off" : "on"
          );
        }}
      />
      <Box>Constraints</Box>
    </FormLabel>
  );
};

const z_date = z
  .string()
  .regex(/^(?:\d{4}-\d{2}-\d{2})?$/, {
    message: "Invalid date",
  })
  .refine(
    (val) => {
      if (!val) return true;

      const [year, month, day] = val.split("-").map(Number);
      const date = new Date(`${year}-${month}-${day}`);
      return (
        !isNaN(date.getTime()) &&
        date.getUTCFullYear() === year &&
        date.getUTCMonth() + 1 === month &&
        date.getUTCDate() === day
      );
    },
    { message: "Invalid date" }
  );

export const FormikSchema = z
  .object({
    dateConstraints: z.discriminatedUnion("switch", [
      z.object({
        switch: z.literal("on"),
        startDate: z_date,
        endDate: z_date,
      }),
      z.object({
        switch: z.literal("off"),
        startDate: z.string(),
        endDate: z.string(),
      }),
    ]),
    dateFormat: z.string(),
  })
  .and(QuestionType.FormikSchema)
  .superRefine((value, ctx) => {
    if (value.type !== "Date") {
      return;
    }
    if (!(DateTypeFormats as readonly string[]).includes(value.dateFormat)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid date format",
        path: ["dateFormat"],
      });
    }
  });

export type FormikType = z.infer<typeof FormikSchema>;

export function toFormValue(question: AF.V2.DateQuestion): FormikType {
  return {
    type: question.type,
    dateFormat: question.constraints?.format ?? DEFAULT_DATE_TYPE_FORMAT,
    dateConstraints: {
      switch:
        isNullOrUndefined(question.constraints) ||
        (question.constraints.endDate === undefined &&
          question.constraints.startDate === undefined)
          ? "off"
          : "on",
      startDate: question.constraints?.startDate ?? "",
      endDate: question.constraints?.endDate ?? "",
    },
  };
}

export function toDateQuestion(
  values: FormikType
): Pick<AF.V2.DateQuestion, "constraints"> {
  if (values.type !== "Date") {
    return { constraints: undefined };
  }

  return {
    constraints: {
      format: values.dateFormat as AF.V2.DateTypeConstraints["format"],
      ...(values.dateConstraints.switch === "on"
        ? {
            startDate: isNonEmptyString(values.dateConstraints.startDate)
              ? values.dateConstraints.startDate
              : undefined,
            endDate: isNonEmptyString(values.dateConstraints.endDate)
              ? values.dateConstraints.endDate
              : undefined,
          }
        : { startDate: undefined, endDate: undefined }),
    },
  };
}

export const EmptyFormikValue: FormikType = {
  type: "FreeText",
  dateFormat: DEFAULT_DATE_TYPE_FORMAT,
  dateConstraints: {
    switch: "off",
    startDate: "",
    endDate: "",
  },
};
